import React, { Component } from 'react';
import styled, { css } from 'styled-components';

interface FjTableCellProps {
  className?: string;
  span?: string;
  // Injected internally by the parent table layout.
  _rowIndex: number;
  _colIndex: number;
  children?: React.ReactNode;
}

interface Alignment {
  row: number;
  col: number;
  span: number;
}

class FjTableCell extends Component<FjTableCellProps> {
  static defaultProps = {
    className: '',
    span: '1',
  };

  get colSpan(): number {
    if (this.props.span === '*') {
      return -1;
    }
    const spanVal = parseInt(this.props.span ?? '', 10);
    if (spanVal > 0) {
      return spanVal;
    }
    throw new Error("Prop 'span' must be '*' or an integer greater than 0");
  }

  render(): React.ReactNode {
    const alignment: Alignment = {
      row: this.props._rowIndex,
      col: this.props._colIndex,
      span: this.colSpan,
    };

    return (
      <CellWrapper className={this.props.className} alignment={alignment}>
        {this.props.children}
      </CellWrapper>
    );
  }
}

export default FjTableCell;

// These theme fields are not part of the base theme; they resolve to
// `undefined` at runtime, matching the original JavaScript behavior.
interface CellTheme {
  borderColor?: string;
  cellPadding?: string;
}

const cellBottomBorder = css`
  solid 1px ${(props) => (props.theme as CellTheme).borderColor}
`;

const colSpanCss = css<{ alignment: Alignment }>`
  ${(props) => (props.alignment.span === -1 ? -1 : props.alignment.col + props.alignment.span)};
`;

const CellWrapper = styled.div<{ alignment: Alignment }>`
  display: flex;
  align-items: center;
  /* A grid item's default min-width is "auto" (its content's intrinsic width), which can force
     the grid track - and the whole table - wider than its container even when the track itself
     is wrap-safe (minmax(0, ...)). min-width: 0 lets this cell's own content wrap/shrink instead. */
  min-width: 0;
  grid-row: ${(props) => props.alignment.row} / ${(props) => props.alignment.row + 1};
  grid-column: ${(props) => props.alignment.col} / ${colSpanCss};
  border-top: ${(props) => (props.alignment.row > 1 ? cellBottomBorder : '')};
  padding: ${(props) => (props.theme as CellTheme).cellPadding};
`;
